import { useState, useRef, useEffect } from "react"
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, Grid, IconButton } from "@mui/material"
import {
  CameraAlt as CameraIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
} from "@mui/icons-material"
import ImageEditorDialog from "./ImageEditorDialog"

const SelfieUploadDialog = ({ open, onClose, onUpload }) => {
  const [mode, setMode] = useState("selection") // 'selection', 'camera', 'upload'
  const [stream, setStream] = useState(null)
  const [capturedImage, setCapturedImage] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(180) // 3 minutes
  const [selectedFile, setSelectedFile] = useState(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const [imageEditorDialog, setImageEditorDialog] = useState(false)
  const [imageToEdit, setImageToEdit] = useState(null)

  useEffect(() => {
    let timer
    if (mode === "camera" && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [mode, timeRemaining]);

  useEffect(() => {
    if (mode !== "camera") return;
    if (!stream) return;
    if (!videoRef.current) return;
  
    const video = videoRef.current;
    video.srcObject = stream;
  
    const play = async () => {
      try {
        await video.play();
        console.log("Video playing");
      } catch (e) {
        console.error("Video play() failed:", e);
      }
    };
  
    play();
  }, [mode, stream]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const startCamera = async () => {
    try {
      setMode("camera");
      setTimeRemaining(180);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user",
        },
        audio: false,
      })

      setStream(mediaStream)

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream

        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          console.log("Video metadata loaded")
          videoRef.current.play()
        }

        videoRef.current.oncanplay = () => {
          console.log("Video can play")
        }
      }

      
    } catch (error) {
      console.error("Error accessing camera:", error)
      alert("Unable to access camera. Please check permissions and try again.")
    }
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
  }

  const capturePhoto = () => {
    console.log("Capture photo clicked")

    if (!videoRef.current || !canvasRef.current) {
      console.error("Video or canvas ref not available")
      alert("Camera not ready. Please try again.")
      return
    }

    const video = videoRef.current
    const canvas = canvasRef.current

    // Check if video is ready
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      console.error("Video not ready")
      alert("Camera is still loading. Please wait a moment and try again.")
      return
    }

    // Check video dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error("Video dimensions are 0")
      alert("Camera not properly initialized. Please try again.")
      return
    }

    console.log("Video dimensions:", video.videoWidth, video.videoHeight)

    try {
      const context = canvas.getContext("2d")

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.log("Photo captured successfully", blob)
            const file = new File([blob], "selfie-capture.jpg", { type: "image/jpeg" })
            setImageToEdit(file)
            setImageEditorDialog(true)
            stopCamera()
          } else {
            console.error("Failed to create blob")
            alert("Failed to capture photo. Please try again.")
          }
        },
        "image/jpeg",
        0.8,
      )
    } catch (error) {
      console.error("Error capturing photo:", error)
      alert("Error capturing photo. Please try again.")
    }
  }

  const retakePhoto = () => {
    setCapturedImage(null)
    startCamera()
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file && file.type.startsWith("image/")) {
      setImageToEdit(file)
      setImageEditorDialog(true)
    }
  }

  const handleImageEditConfirm = (editedFile) => {
    setSelectedFile(editedFile)
    setMode("upload")
    setImageEditorDialog(false)
    setImageToEdit(null)
  }

  const handleImageEditClose = () => {
    setImageEditorDialog(false)
    setImageToEdit(null)
  }

  const handleUpload = () => {
    const fileToUpload = capturedImage || selectedFile
    if (fileToUpload) {
      onUpload(fileToUpload)
      handleClose()
    }
  }

  const handleClose = () => {
    stopCamera()
    setCapturedImage(null)
    setSelectedFile(null)
    setMode("selection")
    setTimeRemaining(180)
    onClose()
  }

  const renderSelectionMode = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <Box
        sx={{
          width: "100%",
          height: 300,
          bgcolor: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
          background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
          borderRadius: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CameraIcon sx={{ fontSize: 80, color: "white", opacity: 0.7 }} />
      </Box>

      {/* <Typography variant="h6" color="error" gutterBottom>
        Time Remaining: {formatTime(timeRemaining)}
      </Typography> */}

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<CameraIcon />}
            onClick={startCamera}
            sx={{
              py: 2,
              bgcolor: "#2196f3",
              "&:hover": { bgcolor: "#1976d2" },
            }}
          >
            Take Selfie Now
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<UploadIcon />}
            component="label"
            sx={{
              py: 2,
              bgcolor: "#37474f",
              "&:hover": { bgcolor: "#263238" },
            }}
          >
            Upload Selfie
            <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
          </Button>
        </Grid>
      </Grid>
    </Box>
  )

  const renderCameraMode = () => (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ position: "relative", mb: 2 }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            maxWidth: 400,
            height: 300,
            objectFit: "cover",
            borderRadius: 8,
            backgroundColor: "#000",
          }}
          onLoadedData={() => console.log("Video loaded")}
          onError={(e) => console.error("Video error:", e)}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />

        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(0,0,0,0.7)",
            color: "white",
            px: 2,
            py: 1,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">Time: {formatTime(timeRemaining)}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
        <Button variant="outlined" onClick={() => setMode("selection")} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button variant="contained" onClick={capturePhoto} startIcon={<CameraIcon />} size="large" disabled={!stream}>
          Capture Photo
        </Button>
      </Box>
    </Box>
  )

  const renderCapturedMode = () => (
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{ mb: 2 }}>
        <img
          src={URL.createObjectURL(capturedImage) || "/placeholder.svg"}
          alt="Captured selfie"
          style={{
            width: "100%",
            maxWidth: 400,
            height: 300,
            objectFit: "cover",
            borderRadius: 8,
          }}
        />
      </Box>

      <Typography variant="body1" gutterBottom>
        Photo captured successfully!
      </Typography>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}>
        <Button variant="outlined" onClick={retakePhoto} startIcon={<RefreshIcon />}>
          Retake
        </Button>
        <Button variant="contained" onClick={handleUpload} startIcon={<CheckIcon />} color="success">
          Use This Photo
        </Button>
      </Box>
    </Box>
  )

  const renderUploadMode = () => (
    <Box sx={{ textAlign: "center" }}>
      {selectedFile && (
        <Box sx={{ mb: 2 }}>
          <img
            src={URL.createObjectURL(selectedFile) || "/placeholder.svg"}
            alt="Selected selfie"
            style={{
              width: "100%",
              maxWidth: 400,
              height: 300,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        </Box>
      )}

      <Typography variant="body1" gutterBottom>
        File selected: {selectedFile?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : 0} MB
      </Typography>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}>
        <Button variant="outlined" onClick={() => setMode("selection")}>
          Choose Different
        </Button>
        <Button variant="contained" onClick={handleUpload} startIcon={<CheckIcon />} color="success">
          Upload This Photo
        </Button>
      </Box>
    </Box>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Selfie Verification</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {mode === "selection" && renderSelectionMode()}
        {mode === "camera" && renderCameraMode()}
        {capturedImage && renderCapturedMode()}
        {mode === "upload" && selectedFile && renderUploadMode()}
      </DialogContent>

      {/* Image Editor Dialog */}
      <ImageEditorDialog
        open={imageEditorDialog}
        onClose={handleImageEditClose}
        imageFile={imageToEdit}
        onConfirm={handleImageEditConfirm}
        title="Edit Selfie"
      />
    </Dialog>
  )
}

export default SelfieUploadDialog
